import * as React from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
  useDismiss,
  useInteractions,
  useHover,
  safePolygon,
} from '@floating-ui/react'
import { Editor } from '@tiptap/react'
import { Copy, Check, ExternalLink, Globe, SquarePen } from 'lucide-react'
import { toast } from 'sonner'
import { useTopicAtomsContext } from '@/hooks/useTopicAtomsContext'
import { Button } from '@/components/ui/button'
import { DocIcon } from '@/components/ui/doc-icon'
import { IconComponent } from '@/components/icon-component'
import './button-hover-card.scss'

interface ButtonHoverCardProps {
  editor: Editor
  onEdit: () => void
}

interface ButtonData {
  href?: string
  text?: string
  variant?: 'primary' | 'secondary'
  icon?: string
  nodePos?: number
}

export function ButtonHoverCard({ editor, onEdit }: ButtonHoverCardProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [isHoveringCard, setIsHoveringCard] = React.useState(false)
  const [buttonElement, setButtonElement] = React.useState<HTMLElement | null>(
    null
  )
  const [buttonData, setButtonData] = React.useState<ButtonData | null>(null)
  const [copied, setCopied] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const copyTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const { allTopicsFlat } = useTopicAtomsContext()

  // Find topic by URL if it's an internal link
  const linkedTopic = React.useMemo(() => {
    if (!buttonData?.href) return null

    // Check if it's an internal topic link
    // const topicMatch = buttonData.href.match(/\/topics\/(.+)/)
    const docId = buttonData?.href
      .split('?')[1]
      ?.split('docId=')[1]
      ?.split('&')[0]
    if (docId) {
      return allTopicsFlat.find((topic) => topic.docId === docId)
    }
    return null
  }, [buttonData, allTopicsFlat])

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom',
    middleware: [offset(5), flip(), shift({ padding: 5 })],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    handleClose: safePolygon({
      blockPointerEvents: false,
      buffer: 1,
    }),
    delay: {
      open: 500,
      close: 0,
    },
    enabled: !!buttonElement,
  })

  const dismiss = useDismiss(context)
  const { getFloatingProps } = useInteractions([hover, dismiss])

  React.useEffect(() => {
    if (!editor || !editor.view) return

    // Use event delegation on the editor container
    const editorDom = editor.view.dom

    const handleMouseEnter = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const button = target.closest(
        'button[data-inline-button]'
      ) as HTMLElement | null

      if (!button) return

      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      const href = button.getAttribute('data-href') || ''
      const text =
        button.getAttribute('data-text') || button.textContent || 'Button'
      const variant = (button.getAttribute('data-variant') || 'primary') as
        | 'primary'
        | 'secondary'
      const icon = button.getAttribute('data-icon') || undefined

      // Find the node position
      const pos = editor.view.posAtDOM(button, 0)

      setButtonData({ href, text, variant, icon, nodePos: pos })
      setButtonElement(button)
      refs.setReference(button)
      setIsOpen(true)
    }

    const handleMouseLeave = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const button = target.closest('button[data-inline-button]')

      if (!button) return

      const relatedTarget = e.relatedTarget as HTMLElement | null

      // Check if we're moving to the hover card
      const isMovingToCard = relatedTarget?.closest('.button-hover-card')

      if (!isMovingToCard) {
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        // Delay closing to allow moving to the hover card
        timeoutRef.current = setTimeout(() => {
          if (!isHoveringCard) {
            setButtonElement(null)
            setButtonData(null)
            setIsOpen(false)
          }
        }, 500)
      }
    }

    // Use event delegation instead of individual listeners
    editorDom.addEventListener('mouseenter', handleMouseEnter, true)
    editorDom.addEventListener('mouseleave', handleMouseLeave, true)

    return () => {
      editorDom.removeEventListener('mouseenter', handleMouseEnter, true)
      editorDom.removeEventListener('mouseleave', handleMouseLeave, true)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [editor, refs, setIsOpen, isHoveringCard])

  const handleEdit = () => {
    setIsOpen(false)
    if (buttonData?.nodePos !== undefined) {
      // Select the button node
      editor.chain().focus().setNodeSelection(buttonData.nodePos).run()

      // Small delay to ensure selection is set before opening editor
      setTimeout(() => {
        onEdit()
      }, 50)
    } else {
      onEdit()
    }
  }

  const handleOpenLink = async () => {
    if (buttonData?.href) {
      // Check if it's an internal link
      if (buttonData.href.startsWith('/')) {
        // Save current doc before navigation if available
        try {
          const editorAny = editor as any
          if (editorAny?.forceSaveDocument) {
            await editorAny.forceSaveDocument()
          }
        } catch (_e) {}
        window.location.href = buttonData.href
      } else {
        // Open external links in new tab
        window.open(buttonData.href, '_blank', 'noopener,noreferrer')
      }
    }
  }

  const handleCopyLink = async () => {
    if (buttonData?.href) {
      try {
        await navigator.clipboard.writeText(buttonData.href)
        setCopied(true)

        // Show success toast
        toast.success('Link copied to clipboard')

        // Close the hover card
        setIsOpen(false)

        // Reset copied state after 2 seconds
        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current)
        }
        copyTimeoutRef.current = setTimeout(() => {
          setCopied(false)
        }, 2000)
      } catch (err) {
        console.error('Failed to copy link:', err)
        toast.error('Failed to copy link')
      }
    }
  }

  React.useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  if (!isOpen || !buttonData || !buttonElement) {
    return null
  }

  const displayTitle = linkedTopic?.title || buttonData.href || 'No link'
  const isInternalLink = !!linkedTopic

  // Parse icon for display
  let iconElement = null
  if (buttonData.icon) {
    let iconValue = buttonData.icon
    let iconCategory: 'EMOJI' | 'LUCIDE' = 'LUCIDE'

    if (buttonData.icon.startsWith('emoji:')) {
      iconValue = buttonData.icon.replace('emoji:', '')
      iconCategory = 'EMOJI'
    }

    iconElement = (
      <IconComponent
        icon={iconValue}
        iconCategory={iconCategory}
        iconType='REGULAR'
        size={16}
      />
    )
  }

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        className='button-hover-card'
        onMouseEnter={() => {
          setIsHoveringCard(true)
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }
        }}
        onMouseLeave={() => {
          setIsHoveringCard(false)
          timeoutRef.current = setTimeout(() => {
            setButtonElement(null)
            setButtonData(null)
            setIsOpen(false)
          }, 300)
        }}
      >
        <div className='button-hover-card-content gap-0.5'>
          {buttonData.href && (
            <div className='button-hover-card-link-info gap-1 p-1'>
              {isInternalLink && linkedTopic ? (
                <DocIcon
                  topic={linkedTopic}
                  size={16}
                  className='!text-muted-foreground'
                />
              ) : (
                <Globe className='!text-muted-foreground' size={16} />
              )}
              <span className='text-popover-foreground max-w-[250px] truncate text-sm leading-5 font-medium'>
                {displayTitle}
              </span>
            </div>
          )}

          <div className='button-hover-card-actions'>
            {buttonData.href && (
              <>
                <Button
                  variant='ghost'
                  onClick={handleOpenLink}
                  size='xs'
                  className='dark:hover:bg-accent!'
                  title={isInternalLink ? 'Go to page' : 'Open link in new tab'}
                >
                  <ExternalLink className='h-4 w-4' />
                </Button>
                <Button
                  variant='ghost'
                  size='xs'
                  onClick={handleCopyLink}
                  title='Copy link'
                  className='dark:hover:bg-accent!'
                >
                  {copied ? (
                    <Check className='h-4 w-4' />
                  ) : (
                    <Copy className='h-4 w-4' />
                  )}
                </Button>
              </>
            )}
            <Button
              variant='ghost'
              size='xs'
              onClick={handleEdit}
              className='dark:hover:bg-accent!'
              title='Edit button'
            >
              <SquarePen size={16} />
            </Button>
          </div>
        </div>
      </div>
    </FloatingPortal>
  )
}
